import { useRef, useState, type FormEvent } from "react";
import { PASSWORD_MIN_LENGTH } from "@longjie/shared";
import { ApiError, request } from "./api";
import { Button, Field, Modal } from "./components";

export function AccountCredentialsModal({
  name,
  username: currentUsername,
  path,
  viewPasswordPath,
  onClose,
  onSaved,
}: {
  name: string;
  username: string;
  path: string;
  viewPasswordPath?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [username, setUsername] = useState(currentUsername);
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const savingRef = useRef(false);
  const [viewedPassword, setViewedPassword] = useState<string | null>(null);
  const [viewMessage, setViewMessage] = useState("");
  const [viewError, setViewError] = useState("");
  const [viewLoading, setViewLoading] = useState(false);

  async function viewPassword() {
    if (!viewPasswordPath || viewLoading) return;
    setViewedPassword(null);
    setViewMessage("");
    setViewError("");
    setViewLoading(true);
    try {
      const result = await request<{ password: string | null; available: boolean }>(viewPasswordPath, { cache: "no-store" });
      if (result.available && result.password !== null) setViewedPassword(result.password);
      else setViewMessage("这个账号还没有可查看的密码记录。以后修改或重新设置密码后，后台才可查看。");
    } catch (caught) {
      setViewError(caught instanceof ApiError ? caught.message : "当前密码暂时无法查看");
    } finally {
      setViewLoading(false);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (savingRef.current) return;
    setError("");
    const nextUsername = username.trim();
    if (!nextUsername || !/^[A-Za-z0-9_.-]+$/.test(nextUsername)) {
      setError("登录账号只能包含字母、数字、下划线、点或短横线");
      return;
    }
    if (newPassword && (newPassword.length < PASSWORD_MIN_LENGTH || newPassword.length > 200)) {
      setError(`新密码需为 ${PASSWORD_MIN_LENGTH} 至 200 位`);
      return;
    }
    if (newPassword && newPassword.toLowerCase() !== confirmation.toLowerCase()) {
      setError("两次输入的新密码不一致");
      return;
    }
    if (nextUsername === currentUsername && !newPassword) {
      setError("请修改登录账号或填写新密码");
      return;
    }
    savingRef.current = true;
    setLoading(true);
    try {
      await request(path, {
        method: "PATCH",
        body: JSON.stringify({
          ...(nextUsername !== currentUsername ? { username: nextUsername } : {}),
          ...(newPassword ? { newPassword } : {}),
        }),
      });
      onSaved();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "账号密码修改失败");
    } finally {
      savingRef.current = false;
      setLoading(false);
    }
  }

  return (
    <Modal
      title="修改账号密码"
      description={`${name}：${viewPasswordPath ? "可查看已保存的当前密码，或直接设置新密码。" : "可直接设置新密码。"}修改后请使用最新登录信息。`}
      onClose={() => { if (!savingRef.current) onClose(); }}
      footer={<>
        <Button variant="secondary" onClick={onClose} disabled={loading}>取消</Button>
        <Button form="account-credentials-form" type="submit" loading={loading}>保存修改</Button>
      </>}
    >
      <form id="account-credentials-form" className="form-grid" onSubmit={submit}>
        {error && <div className="inline-error form-grid__full" role="alert">{error}</div>}
        <div className="form-grid__full">
          <Field label="登录账号">
            <input value={username} onChange={(event) => setUsername(event.target.value)} maxLength={80} autoComplete="off" disabled={loading} required autoFocus />
          </Field>
        </div>
        {viewPasswordPath && <div className="form-grid__full">
          <Button type="button" variant="secondary" onClick={() => void viewPassword()} loading={viewLoading} disabled={loading}>查看当前密码</Button>
          {viewedPassword !== null && <Field label="当前密码"><input value={viewedPassword} type="text" readOnly autoComplete="off" /></Field>}
          {viewMessage && <p className="field__hint" role="status">{viewMessage}</p>}
          {viewError && <div className="inline-error" role="alert">{viewError}</div>}
        </div>}
        <div className="form-grid__full">
          <Field label="新密码" hint={`选填；留空则保留当前密码。至少 ${PASSWORD_MIN_LENGTH} 位，可自由使用字母、数字和符号，不区分字母大小写，数字和符号原样匹配。`}>
            <input type={showNewPassword ? "text" : "password"} value={newPassword} onChange={(event) => setNewPassword(event.target.value)} minLength={PASSWORD_MIN_LENGTH} maxLength={200} autoComplete="new-password" disabled={loading} />
          </Field>
          <label className="check-field"><input type="checkbox" aria-label="显示新密码" checked={showNewPassword} onChange={(event) => setShowNewPassword(event.target.checked)} disabled={loading} />显示新密码</label>
        </div>
        {newPassword && <div className="form-grid__full">
          <Field label="确认新密码">
            <input type={showNewPassword ? "text" : "password"} value={confirmation} onChange={(event) => setConfirmation(event.target.value)} minLength={PASSWORD_MIN_LENGTH} maxLength={200} autoComplete="new-password" disabled={loading} required />
          </Field>
        </div>}
      </form>
    </Modal>
  );
}
